import type { SyncProvider, SyncItem, SyncResult, SyncStatus, SyncedItem } from "../types.js";

const GITHUB_API = "https://api.github.com";

const DEFAULT_STATUS_MAP: Record<string, SyncStatus> = {
  open: "todo",
  closed: "done",
};

const FATHOM_LABEL = "fathom";

async function githubRequest(
  apiKey: string,
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<Record<string, unknown> | Array<Record<string, unknown>>> {
  const res = await fetch(`${GITHUB_API}${path}`, {
    method,
    headers: {
      Accept: "application/vnd.github+json",
      Authorization: `Bearer ${apiKey}`,
      "X-GitHub-Api-Version": "2022-11-28",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GitHub API error (${res.status}): ${text}`);
  }

  return res.json() as Promise<Record<string, unknown> | Array<Record<string, unknown>>>;
}

function buildBody(item: SyncItem): string {
  return `${item.description}

---
_Synced from Fathom_
**Complexity:** ${item.complexity} | **Priority:** ${item.priority} | **Est. Cost:** $${item.estimatedCost.toFixed(2)}
**Acceptance Criteria:** ${item.acceptanceCriteria}
---

**Slice ID:** \`${item.sliceId}\`
**Category:** ${item.category}
**Estimated Tokens:** ${item.estimatedTokens.toLocaleString()}`;
}

export function createGitHubProvider(
  apiKey: string,
  projectKey: string,
  statusMapping?: Record<string, SyncStatus>,
): SyncProvider {
  // projectKey format: "owner/repo"
  const statusMap = { ...DEFAULT_STATUS_MAP, ...statusMapping };

  function mapToFathomStatus(state: string, labels: string[]): SyncStatus {
    // Check labels first for more granular status
    for (const label of labels) {
      if (statusMap[label]) return statusMap[label];
    }
    if (labels.includes("in-progress")) return "in_progress";
    if (labels.includes("in-review")) return "review";
    if (labels.includes("backlog")) return "backlog";
    if (labels.includes("cancelled")) return "cancelled";
    return statusMap[state] ?? "todo";
  }

  async function ensureLabel(label: string): Promise<void> {
    try {
      await githubRequest(apiKey, "GET", `/repos/${projectKey}/labels/${encodeURIComponent(label)}`);
    } catch {
      // Label doesn't exist, create it
      try {
        await githubRequest(apiKey, "POST", `/repos/${projectKey}/labels`, {
          name: label,
          color: "6B5CE7",
          description: "Synced from Fathom",
        });
      } catch {
        // Race condition or permission issue — continue anyway
      }
    }
  }

  return {
    name: "github",

    async createItem(item: SyncItem): Promise<SyncResult> {
      // Ensure the fathom label exists
      await ensureLabel(FATHOM_LABEL);

      const labels = [
        FATHOM_LABEL,
        `complexity:${item.complexity}`,
        `priority:${item.priority}`,
        ...item.labels,
      ];

      const result = await githubRequest(apiKey, "POST", `/repos/${projectKey}/issues`, {
        title: item.title,
        body: buildBody(item),
        labels,
      }) as Record<string, unknown>;

      return {
        externalId: String(result.number),
        url: result.html_url as string,
        provider: "github",
      };
    },

    async updateStatus(externalId: string, status: SyncStatus): Promise<void> {
      const state = status === "done" || status === "cancelled" ? "closed" : "open";

      await githubRequest(apiKey, "PATCH", `/repos/${projectKey}/issues/${externalId}`, {
        state,
      });

      // Also add a status label
      const statusLabel = status === "in_progress" ? "in-progress" : status;
      await ensureLabel(statusLabel);

      // Get current labels to avoid duplicates
      const issue = await githubRequest(
        apiKey,
        "GET",
        `/repos/${projectKey}/issues/${externalId}`,
      ) as Record<string, unknown>;
      const currentLabels = (issue.labels as Array<{ name: string }>).map((l) => l.name);

      // Remove old status labels
      const statusLabels = ["backlog", "todo", "in-progress", "review", "done", "cancelled"];
      const filteredLabels = currentLabels.filter((l) => !statusLabels.includes(l));
      filteredLabels.push(statusLabel);

      await githubRequest(apiKey, "PATCH", `/repos/${projectKey}/issues/${externalId}`, {
        labels: filteredLabels,
      });
    },

    async getStatus(externalId: string): Promise<SyncStatus> {
      const issue = await githubRequest(
        apiKey,
        "GET",
        `/repos/${projectKey}/issues/${externalId}`,
      ) as Record<string, unknown>;

      const state = issue.state as string;
      const labels = (issue.labels as Array<{ name: string }>).map((l) => l.name);

      return mapToFathomStatus(state, labels);
    },

    async listItems(): Promise<SyncedItem[]> {
      const issues = await githubRequest(
        apiKey,
        "GET",
        `/repos/${projectKey}/issues?labels=${FATHOM_LABEL}&state=all&per_page=100`,
      ) as Array<Record<string, unknown>>;

      return issues.map((issue) => {
        const labels = (issue.labels as Array<{ name: string }>).map((l) => l.name);
        return {
          sliceId: String(issue.number),
          externalId: String(issue.number),
          url: issue.html_url as string,
          status: mapToFathomStatus(issue.state as string, labels),
          lastSynced: new Date(issue.updated_at as string).getTime(),
        };
      });
    },
  };
}
